import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { DollarSign, AlertTriangle, ShieldCheck, ChefHat, Wine, Flame, TrendingUp, Search, User, Calendar, Receipt as ReceiptIcon, X } from 'lucide-react';

const GOLD = '#C7871F';
const BLUE = '#007AFF';
const GREEN = '#10B981';
const AMBER = '#F59E0B';
const RED = '#EF4444';
const PIE_COLORS = [GOLD, BLUE, GREEN, AMBER, RED, '#8B5CF6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-bg-secondary border border-white/10 rounded-sm p-2 text-xs font-sans">
      <p className="text-zinc-400">{label}</p>
      {payload.map((p, i) => <p key={`${p.name}-${i}`} style={{ color: p.color }}>{p.name}: {typeof p.value === 'number' ? `$${p.value.toFixed(2)}` : p.value}</p>)}
    </div>
  );
};

export default function DirectorDashboard() {
  const [overview, setOverview] = useState({});
  const [revenue, setRevenue] = useState({});
  const [timing, setTiming] = useState({});
  // Tabs drill-down state — search box + click-to-open customer history dialog.
  const [tabSearch, setTabSearch] = useState('');
  const [customerDetail, setCustomerDetail] = useState(null); // { name, loading, data, error }

  const fetchAnalytics = useCallback(async () => {
    try {
      const [ovRes, revRes, timRes] = await Promise.all([
        api.get('/analytics/overview'), api.get('/analytics/revenue'), api.get('/analytics/timing')
      ]);
      setOverview(ovRes.data); setRevenue(revRes.data); setTiming(timRes.data);
    } catch (err) { console.error('fetch analytics failed:', err); }
  }, []);

  useEffect(() => { fetchAnalytics(); const i = setInterval(fetchAnalytics, 30000); return () => clearInterval(i); }, [fetchAnalytics]);

  // Customer-tab drill-down: open the dialog and lazy-fetch the all-time
  // history for this name. Walk-in guests don't share a stable customer_id
  // so we match by display name (case-insensitive) on the backend.
  const openCustomerDrillDown = useCallback(async (name) => {
    if (!name) return;
    setCustomerDetail({ name, loading: true, data: null, error: null });
    try {
      const { data } = await api.get('/analytics/customer-tabs', { params: { name } });
      setCustomerDetail({ name, loading: false, data, error: null });
    } catch (e) {
      setCustomerDetail({ name, loading: false, data: null, error: e.response?.data?.detail || 'Failed to load history' });
    }
  }, []);

  const METHOD_LABELS = { cash: 'Cash (no promo)', cash_promo: 'Cash · 10% Promo', card: 'EFTPOS', apple_pay: 'Apple Pay', tab: 'Tab' };
  const methodData = revenue?.by_method
    ? Object.entries(revenue.by_method).map(([name, value]) => ({ name: METHOD_LABELS[name] || name, value }))
    : [];
  const paymentMix = revenue?.payment_mix || [];
  // Pretty labels for the by_category chart. `manual_charge` is the
  // manager-only ad-hoc bucket (Custom Product) — kept distinct from menu
  // categories so it doesn't pollute true sales analytics.
  const CATEGORY_LABELS = {
    food: 'Food',
    drinks: 'Drinks',
    shisha: 'Shisha',
    activities: 'Activities',
    manual_charge: 'Misc/Manual',
  };
  const categoryData = revenue?.by_category
    ? Object.entries(revenue.by_category).map(([name, value]) => ({ name: CATEGORY_LABELS[name] || name, value }))
    : [];
  const dailyData = revenue?.daily || [];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Revenue', value: `$${(overview?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-gold' },
          { label: 'Today Revenue', value: `$${(overview?.today_revenue || 0).toFixed(2)}`, icon: TrendingUp, color: 'text-emerald-400' },
          { label: 'Total Orders', value: overview?.total_orders || 0, icon: ShieldCheck, color: 'text-blue-accent' },
          { label: 'Avg Order', value: `$${(overview?.avg_order_value || 0).toFixed(2)}`, icon: DollarSign, color: 'text-gold' },
        ].map((s) => (
          <div key={s.label} className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-1">
              <s.icon className={`w-4 h-4 ${s.color}`} />
              <span className="text-[11px] text-zinc-500 font-sans uppercase tracking-wider">{s.label}</span>
            </div>
            <p className="text-2xl font-sans font-semibold text-white" data-testid={`analytics-${s.label.toLowerCase().replace(/\s/g, '-')}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
          <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider mb-4">Daily Revenue (14 days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fill: '#71717A', fontSize: 10 }} tickFormatter={v => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="revenue" stroke={GOLD} strokeWidth={2} dot={{ fill: GOLD, r: 3 }} name="Revenue" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
          <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider mb-4">Revenue by Payment Method</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={methodData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: $${value.toFixed(0)}`} labelLine={{ stroke: 'rgba(255,255,255,0.2)' }}>
                {methodData.map((entry, i) => <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          {/* Payment Mix breakdown — counts + $ + % share so owner can */}
          {/* track Cash vs EFTPOS vs Apple Pay vs Tab conversion at a glance. */}
          <div className="mt-3 space-y-1.5" data-testid="payment-mix-list">
            {paymentMix.map((m, i) => (
              <div key={m.key} className="flex items-center justify-between text-xs font-sans" data-testid={`payment-mix-row-${m.key}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-white">{m.label}</span>
                  <span className="text-zinc-500">· {m.count} {m.count === 1 ? 'order' : 'orders'}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-zinc-300 font-mono">${m.total.toFixed(2)}</span>
                  <span className="text-zinc-500 font-mono w-12 text-right">{m.pct_count.toFixed(0)}%</span>
                </div>
              </div>
            ))}
            {paymentMix.length === 0 && (
              <p className="text-xs text-zinc-500 font-sans italic">No paid orders yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs revenue breakdown — per owner request, tab orders stay
          grouped in reporting. Each row is one customer tab, not N rows
          per order on the tab. Search + click drills into the customer's
          lifetime tab history. Hidden when no tabs settled in range. */}
      {revenue?.tabs?.count > 0 && (
        <div className="bg-white/[0.03] border border-blue-accent/30 rounded-sm p-4" data-testid="tabs-revenue-block">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <h3 className="text-sm font-sans text-zinc-300 uppercase tracking-wider">
              Tabs — Grouped Revenue
            </h3>
            <span className="text-xs font-sans text-zinc-500">
              {revenue.tabs.count} tab{revenue.tabs.count === 1 ? '' : 's'} · ${Number(revenue.tabs.revenue || 0).toFixed(2)}
              {' · '}
              {revenue.tabs.direct_orders} direct order{revenue.tabs.direct_orders === 1 ? '' : 's'} · ${Number(revenue.tabs.direct_revenue || 0).toFixed(2)}
            </span>
          </div>
          {/* Search — instant client-side filter on customer name. Bigger
              than 30 rows? Filtering keeps the dashboard scannable. */}
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
            <Input
              value={tabSearch}
              onChange={e => setTabSearch(e.target.value)}
              placeholder="Search tabs by customer name…"
              className="pl-8 h-9 bg-bg-primary border-white/10 focus:border-gold text-sm font-sans"
              data-testid="tabs-revenue-search"
            />
            {tabSearch && (
              <button
                type="button"
                onClick={() => setTabSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                data-testid="tabs-revenue-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="tabs-revenue-list">
            {(() => {
              const q = tabSearch.trim().toLowerCase();
              const list = q
                ? (revenue.tabs.list || []).filter(t => (t.customer_name || '').toLowerCase().includes(q))
                : (revenue.tabs.list || []);
              if (list.length === 0) {
                return <p className="text-xs text-zinc-500 font-sans italic px-2 py-3" data-testid="tabs-revenue-empty">No tabs match "{tabSearch}"</p>;
              }
              return list.map(t => (
                <button
                  type="button"
                  key={t.tab_id}
                  onClick={() => openCustomerDrillDown(t.customer_name)}
                  className="w-full flex items-center justify-between gap-3 text-xs font-sans px-2 py-1.5 rounded-sm bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.05] hover:border-gold/30 transition-colors text-left"
                  data-testid={`tabs-revenue-row-${t.tab_id}`}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <User className="w-3 h-3 text-zinc-500 shrink-0" />
                    <span className="text-white truncate">{t.customer_name}</span>
                    <span className="text-zinc-500">· {t.orders} order{t.orders === 1 ? '' : 's'}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[10px] uppercase tracking-[0.1em] text-zinc-400">{t.final_payment_method}</span>
                    <span className="text-gold font-mono font-semibold w-20 text-right">${Number(t.total).toFixed(2)}</span>
                  </div>
                </button>
              ));
            })()}
          </div>
          <p className="text-[10px] text-zinc-500 font-sans italic mt-2">Tap a tab to see this customer's lifetime visit history.</p>
        </div>
      )}

      {/* Customer Tab History — drill-down dialog. Surfaces lifetime tab
          stats so the director can spot regulars and comp VIPs. */}
      <Dialog open={!!customerDetail} onOpenChange={(o) => { if (!o) setCustomerDetail(null); }}>
        <DialogContent className="bg-bg-secondary border border-blue-accent/30 max-w-2xl" data-testid="customer-tab-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <User className="w-4 h-4 text-gold" />
              {customerDetail?.name} · Tab History
            </DialogTitle>
          </DialogHeader>
          {customerDetail?.loading && (
            <div className="flex items-center justify-center py-8" data-testid="customer-tab-history-loading">
              <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {customerDetail?.error && (
            <p className="text-xs text-red-300 font-sans bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2">{customerDetail.error}</p>
          )}
          {customerDetail?.data && (() => {
            const d = customerDetail.data;
            const first = d.first_visit ? new Date(d.first_visit).toLocaleDateString('en-AU') : '—';
            const last = d.last_visit ? new Date(d.last_visit).toLocaleDateString('en-AU') : '—';
            return (
              <div className="space-y-4 pt-1">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="customer-tab-history-stats">
                  <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans font-bold">Tabs Opened</p>
                    <p className="text-lg font-serif text-white mt-1" data-testid="ctab-stat-count">{d.tabs_count}</p>
                  </div>
                  <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans font-bold">Lifetime Spend</p>
                    <p className="text-lg font-serif text-gold mt-1" data-testid="ctab-stat-revenue">${Number(d.total_revenue).toFixed(2)}</p>
                  </div>
                  <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans font-bold">Avg / Tab</p>
                    <p className="text-lg font-serif text-blue-accent mt-1" data-testid="ctab-stat-avg">${Number(d.avg_per_tab).toFixed(2)}</p>
                  </div>
                  <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans font-bold">Favourite</p>
                    <p className="text-lg font-serif text-emerald-300 mt-1 uppercase">{d.favourite_method || '—'}</p>
                  </div>
                </div>
                <p className="text-[11px] text-zinc-500 font-sans flex items-center gap-1.5" data-testid="ctab-stat-visits">
                  <Calendar className="w-3 h-3" /> First visit {first} · Last visit {last} · {d.total_orders} orders across {d.tabs_count} tab{d.tabs_count === 1 ? '' : 's'}
                </p>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans font-bold mb-1.5">All Tabs (newest first)</p>
                  <div className="space-y-1 max-h-72 overflow-y-auto pr-1" data-testid="ctab-tab-list">
                    {d.tabs.map(t => (
                      <div key={t.tab_id} className="flex items-center justify-between gap-2 text-xs font-sans px-2 py-1.5 rounded-sm bg-white/[0.02] border border-white/[0.04]" data-testid={`ctab-tab-row-${t.tab_id}`}>
                        <div className="min-w-0 flex items-center gap-2">
                          <ReceiptIcon className="w-3 h-3 text-zinc-500 shrink-0" />
                          <span className="text-zinc-300">{t.settled_at ? new Date(t.settled_at).toLocaleDateString('en-AU') : '—'}</span>
                          <span className="text-zinc-500">· {t.orders} ord</span>
                          {t.tables?.length > 0 && <span className="text-zinc-500">· T{t.tables.join(', T')}</span>}
                          {t.discount_total > 0 && <span className="text-pink-300">· −${Number(t.discount_total).toFixed(2)}</span>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[10px] uppercase tracking-[0.1em] text-zinc-400">{t.final_payment_method || 'tab'}</span>
                          <span className="text-gold font-mono font-semibold w-20 text-right">${Number(t.total).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
          <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider mb-4">Revenue by Category</h3>          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={categoryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill: '#71717A', fontSize: 11 }} />
              <YAxis tick={{ fill: '#71717A', fontSize: 10 }} tickFormatter={v => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={GOLD} radius={[2, 2, 0, 0]} name="Revenue" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
          <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider mb-4">Top Selling Items</h3>
          <div className="space-y-2">
            {(overview?.top_items || []).map((item, i) => (
              <div key={item.name || i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-gold font-mono text-xs w-5">#{i + 1}</span>
                  <span className="text-white font-sans text-sm">{item.name}</span>
                </div>
                <span className="text-zinc-400 font-mono text-sm">{item.count} sold</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timing */}
      <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
        <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider mb-4">Preparation Timing</h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { cat: 'food', icon: ChefHat, label: 'Food', threshold: 10 },
            { cat: 'drinks', icon: Wine, label: 'Drinks', threshold: 5 },
            { cat: 'shisha', icon: Flame, label: 'Shisha', threshold: 10 },
          ].map(({ cat, icon: Icon, label, threshold }) => {
            const avg = timing?.avg_completion_time?.[cat] || 0;
            const delayed = timing?.delayed_orders?.[cat] || 0;
            const isOver = avg > threshold;
            return (
              <div key={cat} className="text-center">
                <Icon className={`w-6 h-6 mx-auto mb-2 ${isOver ? 'text-red-400' : 'text-emerald-400'}`} />
                <p className="text-xs text-zinc-500 font-sans uppercase tracking-wider">{label}</p>
                <p className={`text-2xl font-mono ${isOver ? 'text-red-400' : 'text-white'}`}>{avg.toFixed(1)}m</p>
                <p className="text-xs text-zinc-600 font-sans">Target: {threshold}m</p>
                {delayed > 0 && (
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <AlertTriangle className="w-3 h-3 text-amber-400" />
                    <span className="text-amber-400 text-xs font-sans">{delayed} delayed</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4 text-center">
          <p className="text-xs text-zinc-500 font-sans uppercase tracking-wider">Week Orders</p>
          <p className="text-2xl font-sans font-semibold text-white">{overview?.week_orders || 0}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4 text-center">
          <p className="text-xs text-zinc-500 font-sans uppercase tracking-wider">Completed</p>
          <p className="text-2xl font-sans font-semibold text-emerald-400">{overview?.completed_orders || 0}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4 text-center">
          <p className="text-xs text-zinc-500 font-sans uppercase tracking-wider">Voided</p>
          <p className="text-2xl font-sans font-semibold text-red-400">{overview?.voided_orders || 0}</p>
        </div>
      </div>
    </div>
  );
}
